import type { DeploymentGroup, DeploymentGroupCurrent } from '../api';
import { statusFor } from '../labels';

/**
 * A finished coordinated deployment has two separate facts (observation
 * 10): how the group went at the time, and what its components serve now.
 * historyLabel names the first as history once the present differs;
 * nowValue is the second, for the groupCurrent status kind.
 */
export function groupHistoryLabel(group: DeploymentGroup): string {
  const label = statusFor('deploymentGroup', group.status).label;
  return group.current && differsFromNow(group) ? `${label} then` : label;
}

export function groupNowValue(group: DeploymentGroup): 'all_live' | 'not_all_live' | null {
  if (!group.current || !differsFromNow(group)) return null;
  return group.current.all_live ? 'all_live' : 'not_all_live';
}

/** "Partly live" and "all live now" disagree, or something changed since. */
function differsFromNow(group: DeploymentGroup): boolean {
  const current = group.current as DeploymentGroupCurrent;
  return group.status !== 'completed' || !current.all_live || current.superseded;
}

/** What one member's component serves now, in words. */
export function memberNowSentence(liveNow: DeploymentGroupCurrent['members'][number]['live_now']): string {
  switch (liveNow) {
    case 'this_release':
      return 'The release this deployment shipped is live.';
    case 'newer_release':
      return 'Live now from a later deployment.';
    case 'previous_release':
      return 'Not shipped: the release before it is still live.';
    default:
      return 'Nothing of this component is live now.';
  }
}
